/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, map } from 'rxjs';

import { IImportTicketsStateAPI } from './import-tickets-state.service';
import { Ticket } from '@touchplan/common';
/**
 * This service facilitates communication between the "import tickets" module and the rest of the app
 * after its initialization.
 */
@Injectable({
	providedIn: 'root',
})
export class ImportTicketsCommonService {
	private _importTicketsDialogStateAPIInstances: {
		dialogId: string;
		importTicketsStateAPI: IImportTicketsStateAPI;
	}[] = [];
	private _isInsertionPointVisibleSubject$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private _insertionPointYSubject$: BehaviorSubject<number> = new BehaviorSubject(null);
	private _isInsertionPointVisibleSubscription: Subscription;
	private _insertionPointYSubscription: Subscription;
	private _swimlanesPositionsSubject$: BehaviorSubject<number[]> = new BehaviorSubject([]);
	private _swimlanesPositionsSubscription: Subscription;
	public isInsertionPointVisible$: Observable<boolean> = this._isInsertionPointVisibleSubject$.asObservable();
	public insertionPointY$: Observable<number> = this._insertionPointYSubject$.asObservable();
	public swimlanesPositions$: Observable<number[]> = this._swimlanesPositionsSubject$.asObservable();
	public focusTicket$ = new Subject<Ticket>();

	private throwDialogStateAPINotInstantiatedError(): void {
		throw new Error('No instance of the Import Tickets dialog state API has been instantiated.');
	}

	private throwDialogStateAPIMaxInstancesError(): void {
		throw new Error('The system only supports one instance of the dialog instance state API at a time.');
	}

	public registerImportTicketsDialogStateAPI(
		dialogId: string,
		importTicketsStateAPI: IImportTicketsStateAPI
	): void {
		// Currently, the service only allows one instance at a time; however, in the future, it may support multiple instances.
		if (this._importTicketsDialogStateAPIInstances.length === 0) {
			this._importTicketsDialogStateAPIInstances.push({
				dialogId,
				importTicketsStateAPI,
			});
			this.subscribeDialogStateAPI(this._importTicketsDialogStateAPIInstances[0].importTicketsStateAPI);
		} else {
			this.throwDialogStateAPIMaxInstancesError();
		}
	}

	public unRegisterImportTicketsDialogStateAPI(unRegisterDialogId: string): void {
		if (this._importTicketsDialogStateAPIInstances.find(({ dialogId }) => dialogId === unRegisterDialogId)) {
			// set default values
			this._isInsertionPointVisibleSubject$.next(false);
			this._insertionPointYSubject$.next(null);

			// unsubscribe current subscriptions
			this._isInsertionPointVisibleSubscription.unsubscribe();
			this._insertionPointYSubscription.unsubscribe();
			this._swimlanesPositionsSubscription.unsubscribe();

			// remove the current dialog instance API dialog
			this._importTicketsDialogStateAPIInstances = this._importTicketsDialogStateAPIInstances.filter(
				({ dialogId }) => dialogId !== unRegisterDialogId
			);
		} else {
			throw new Error(
				`There are no dialog instances of the Import Tickets dialog state API with the identifier ${unRegisterDialogId}.`
			);
		}
	}

	private subscribeDialogStateAPI(dialogStateAPI: IImportTicketsStateAPI): void {
		this._isInsertionPointVisibleSubscription = dialogStateAPI.isInsertionPointVisible.subscribe({
			next: (isVisible) => {
				this._isInsertionPointVisibleSubject$.next(isVisible);
			},
			error: (e) => {
				this._isInsertionPointVisibleSubject$.error(e);
			},
		});
		this._insertionPointYSubscription = dialogStateAPI.insertionPointY.subscribe({
			next: (y) => {
				this._insertionPointYSubject$.next(y);
			},
			error: (e) => {
				this._insertionPointYSubject$.error(e);
			},
		});
		this._swimlanesPositionsSubscription = dialogStateAPI.swimlaneList
			.pipe(
				map((swimlaneList) =>
					Array.from(swimlaneList.values()).reduce((acc, swimlane) => {
						acc.push(swimlane.view.screenTop, swimlane.view.screenBottom);
						return acc;
					}, [])
				)
			)
			.subscribe(this._swimlanesPositionsSubject$);
	}

	public setInsertionPointY(pointY: number): void {
		if (!this._importTicketsDialogStateAPIInstances[0]) {
			this.throwDialogStateAPINotInstantiatedError();
		}
		this._importTicketsDialogStateAPIInstances[0].importTicketsStateAPI.setInsertionPointY(pointY);
	}

	public focusTicket(ticket: Ticket) {
		this.focusTicket$.next(ticket);
	}
}
